import {
  __federation_method_getRemote as getRemote,
  __federation_method_setRemote as setRemote,
  __federation_method_unwrapDefault as unwrapDefault,
} from 'virtual:__federation__';
import { REMOTE_CONFIGS } from '../config/remoteConfig';

// Generic interface for remote configurations
export interface RemoteConfig {
  url: string | (() => Promise<string>);
}

/**
 * Remote loader utility for dynamically loading micro frontends
 */
export class RemoteLoader {
  private static loadedRemotes = new Set<string>();

  /**
   * Loads a remote component dynamically with fallback handling
   * @param remoteName - Name of the remote application
   * @param module - Module path to load (e.g., './App')
   * @param config - Remote configuration
   * @returns Promise that resolves to the remote component or null if failed
   */
  static async loadRemote(remoteName: string, module: string): Promise<any | null> {
    try {
      const config = getRemoteConfig(remoteName);

      if (!config) {
        throw new Error(`No remote config found for ${remoteName}`);
      }

      // Set remote configuration if not already loaded
      if (!this.loadedRemotes.has(remoteName)) {
        setRemote(remoteName, {
          url: config.url,
          format: 'esm',
          from: 'vite',
        });
        this.loadedRemotes.add(remoteName);
      }

      // Load remote module
      const remoteComponent = await getRemote(remoteName, module).then((remoteModule) => unwrapDefault(remoteModule));

      console.log(`Successfully loaded remote ${remoteName}/${module}`);
      return remoteComponent;
    } catch (error) {
      console.warn(`Failed to load remote ${remoteName}/${module}:`, error);
      return null;
    }
  }

  /**
   * Check if a remote is available without loading it
   * @param remoteName - Name of the remote application
   * @param config - Remote configuration
   * @returns Promise that resolves to boolean indicating availability
   */
  static async isRemoteAvailable(remoteName: string, config: RemoteConfig): Promise<boolean> {
    try {
      const url = typeof config.url === 'function' ? await config.url() : config.url;

      const response = await fetch(url, {
        method: 'HEAD',
      });

      return response.ok;
    } catch (error) {
      console.warn(`Remote ${remoteName} is not available:`, error);
      return false;
    }
  }

  /**
   * Reset loaded remotes (useful for development)
   */
  static resetLoadedRemotes(): void {
    this.loadedRemotes.clear();
  }
}

/**
 * Get configuration for a specific remote app
 */
export const getRemoteConfig = (appKey: string): RemoteConfig | null => {
  return REMOTE_CONFIGS[appKey] || null;
};

/**
 * Get all configured remotes
 */
export const getAllRemotes = (): Record<string, RemoteConfig> => {
  return REMOTE_CONFIGS;
};
